"use client";
import { Flex } from "@/app/[lang]/components/ui/AntdLite";
import Link from "next/link";
import classnames from "classnames";
import type { RefObject} from "react";
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "@/app/[lang]/utils/dompurify";
import type { SnapCarouselHandle } from "@/app/[lang]/components/ui/SnapCarousel";
import { useMediaQuery } from "react-responsive";

import styles from "./Slide.module.scss";

// Helpers
import { getImageScalingClassname } from "@/app/[lang]/utils/getImageScalingClassname";
import { getStrapiMedia } from "@/app/[lang]/utils/api-helpers";
import { optimizedSrc, optimizedSrcSet } from "@/app/[lang]/utils/optimized-image-src";

// Components
import Button from "../../ui/Button";
import type { HeroProps, Picture } from "@/app/[lang]/components/Hero/interface";
import { AppStoreButton } from "@/app/[lang]/components/AppStoreButton";
import { GooglePlayButton } from "@/app/[lang]/components/GooglePlayButton";
import type { ButtonProps } from "@/app/[lang]/components/ui/Button/interface";
import ArrowIcon from "@/app/[lang]/components/ui/ArrowIcon";
import VideoEmbed from "@/app/[lang]/components/VideoEmbed";

type SlideProps = HeroProps & {
  carouselRef: RefObject<SnapCarouselHandle>,
  showArrows: boolean,
  imageLoading: React.ImgHTMLAttributes<HTMLImageElement>['loading']
  isFirstSlide?: any;
}

const Slide:React.FC<SlideProps> = (props) => {
  const { data, carouselRef, showArrows, imageLoading } = props
  const { size, showMobileDescription } = data;

  return (
    <div className={ styles.slideWrapper }>
      <Flex className={ classnames( styles.slideContentWrapper, size && styles[size], 'text-white' ) }>

        <div className={ classnames( styles.slideContent ) }>
          <div className={ classnames( styles.slideContentText ) }>
            <div className={ classnames( styles.slideTitle, 'h1' ) } dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.title) }} />
            <div className={ classnames( styles.description, {
              'hide-on-mobile': ! showMobileDescription
            } ) } dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.description) }} />
          </div>

          { !!data?.buttons?.length &&
            <div className={ styles.slideButtons }>
              { data?.buttons.map((button: ButtonProps["attributes"]) => {
                const key = `${button.type ?? "button"}-${button.text ?? ""}-${button.url ?? ""}`;
                return <SlideButton key={ key } { ...button } />;
              })}
            </div>
          }

          { showArrows && (
            <div className={ styles.sliderArrows }>
              <ArrowIcon
                color="white"
                direction="left"
                onClick={() => carouselRef.current?.prev()}
              />
              <ArrowIcon
                color="white"
                direction="right"
                onClick={() => carouselRef.current?.next()}
              />
            </div>
          )}
        </div>
      </Flex>

      {data.mobilePicture?.data || data.picture?.data ?
          <SlideMedia
              overlay={data.overlay}
              picture={data.picture}
              mobilePicture={data.mobilePicture}
              imageScaling={data.imageScaling}
              loading={imageLoading}
              isFirstSlide={props.isFirstSlide}
          /> :
          <SliderVideo
              media_embedded={data.media_embedded}
              mobile_media_embedded={data.mobile_media_embedded}
          />
      }
    </div>
  );
}

type SlideImageProps = {
  picture?: Picture  | null | undefined,
  mobilePicture?: Picture | null | undefined,
  overlay: boolean,
  imageScaling: HeroProps['data']['imageScaling'],
  isFirstSlide?: any;
  loading: React.ImgHTMLAttributes<HTMLImageElement>['loading']
}

const SlideMedia:React.FC<SlideImageProps> = (props) => {
  const { picture, mobilePicture, overlay, imageScaling, loading, isFirstSlide } = props;

  // Art direction without JS, same as Hero: the mobile crop is its own asset in
  // Strapi and used to be ignored here, so phones downloaded the desktop image
  // (differently framed, several times heavier). A native <picture> fetches only
  // the source matching the viewport, and either side falls back to the other
  // when the editor filled in just one.
  const desktopPicture = picture?.data ?? mobilePicture?.data;
  const primaryPicture = mobilePicture?.data ?? picture?.data;

  const desktopSrc = getStrapiMedia(desktopPicture?.attributes?.url);
  const mediaSrc = getStrapiMedia(primaryPicture?.attributes?.url);

  const scaleClassname = useMemo(() => {
    return getImageScalingClassname(imageScaling)
  }, [imageScaling]);

  if ( ! mediaSrc || ! primaryPicture ) {
    return null
  }

  const mime = primaryPicture.attributes?.mime;

  if ( mime?.startsWith("video") ) {
    return (
      <div className={ styles.slideImageWrapper }>
        <video className={styles.videoWrapper} controls width="100%">
          <source src={mediaSrc} type={ mime } />
          Your browser does not support the video tag.
        </video>
        { overlay && <div className={ styles.slideImageOverlay }></div> }
      </div>
    )
  }

  return (
    <div className={ styles.slideImageWrapper }>
      <picture>
        { desktopSrc && (
          <source
            media="(min-width: 900px)"
            srcSet={ optimizedSrcSet(desktopSrc, [1024, 1280, 1920]) }
            sizes="50vw"
            width={ desktopPicture?.attributes?.width }
            height={ desktopPicture?.attributes?.height }
          />
        )}
        <img
          className={ scaleClassname }
          src={ optimizedSrc(mediaSrc, 768) }
          srcSet={ optimizedSrcSet(mediaSrc, [640, 768, 1024]) }
          sizes="100vw"
          width={ primaryPicture.attributes?.width }
          height={ primaryPicture.attributes?.height }
          alt={ primaryPicture.attributes?.alternativeText ?? "PPC Blue Romania" }
          fetchPriority={ isFirstSlide ? 'high' : 'auto' }
          loading={ isFirstSlide ? 'eager' : loading }
          decoding="async"
        />
      </picture>
      { overlay && <div className={ styles.slideImageOverlay }></div> }
    </div>
  )
}

const SlideButton:React.FC<ButtonProps["attributes"]> = ( props ) => {
  const { type, url, hidden } = props;
  const href = url ?? "";
  const isExternal = /^https?:\/\//.test(href);

  if ( !!hidden ) {
    return null;
  }

  if ( type === "applestore" ) {
    if (isExternal) {
      return (
        <a href={href} className={styles.buttonStore} target="_blank" rel="noopener noreferrer">
          <AppStoreButton />
        </a>
      );
    }

    return (
        <Link href={href} className={styles.buttonStore}>
          <AppStoreButton />
        </Link>
    )
  }

  if ( type === "playstore" ) {
    if (isExternal) {
      return (
        <a href={href} className={styles.buttonStore} target="_blank" rel="noopener noreferrer">
          <GooglePlayButton />
        </a>
      );
    }

    return (
        <Link href={href} className={styles.buttonStore}>
          <GooglePlayButton />
        </Link>
    )
  }

  return <Button attributes={ props } className={styles.buttonCTA}/>
}

const SliderVideo = ({ media_embedded, mobile_media_embedded } : any ) => {
  const [client, setClient] = useState(false);
  const isLarge = useMediaQuery({ minWidth: 900 });

  useEffect(() => {
    setClient(true)
  }, [])

  const url = useMemo(() => {

    if ( !client ) {
      return null;
    }

    if ( isLarge ) {
      return media_embedded ?? mobile_media_embedded;
    }

    return mobile_media_embedded ?? media_embedded

  }, [mobile_media_embedded, media_embedded, isLarge, client]);

  return (
      <div className={styles.slideVideo}>
        <VideoEmbed url={url} />
      </div>
  )
}

export default Slide
